import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import AppTextInput from './AppTextInput';
import {
  BUSINESS_CATEGORIES,
  getCategoryName,
  searchCategories,
} from '../constants/businessCategories';

export interface BusinessCategoryPickerProps {
  categoryId: string;
  onSelect: (categoryId: string, categoryName: string) => void;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  label?: string;
}

const BusinessCategoryPicker: React.FC<BusinessCategoryPickerProps> = ({
  categoryId,
  onSelect,
  disabled = false,
  error,
  helperText = 'Search how you describe your business (e.g. car mechanic, hair salon). This controls booking features like vehicle details.',
  label = 'Industry',
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const displayLabel = categoryId ? getCategoryName(categoryId) : '';

  const filteredCategories = useMemo(() => {
    if (searchQuery.trim()) {
      return searchCategories(searchQuery) || [];
    }
    return BUSINESS_CATEGORIES;
  }, [searchQuery]);

  const handleSelect = (id: string, name: string) => {
    onSelect(id, name);
    setOpen(false);
    setSearchQuery('');
  };

  if (disabled) {
    return (
      <View style={styles.container}>
        {label ? <Text style={styles.label}>{label}</Text> : null}
        <Text style={styles.valueText}>{displayLabel || 'Not set'}</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      <TouchableOpacity
        style={[styles.selector, error ? styles.selectorError : null]}
        onPress={() => setOpen((prev) => !prev)}
        accessibilityRole="button"
        accessibilityLabel="Select business industry"
      >
        <Text style={displayLabel ? styles.selectedText : styles.placeholderText}>
          {displayLabel || 'Search and select your industry'}
        </Text>
        <Text style={styles.dropdownIcon}>{open ? '▲' : '▼'}</Text>
      </TouchableOpacity>
      {helperText ? <Text style={styles.helperText}>{helperText}</Text> : null}
      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      {open ? (
        <View style={styles.dropdown}>
          <View style={styles.searchRow}>
            <Text style={styles.searchIcon}>🔍</Text>
            <AppTextInput
              style={styles.searchInput}
              placeholder="Search (e.g. car mechanic, salon)..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>
          <ScrollView style={styles.list} nestedScrollEnabled keyboardShouldPersistTaps="handled">
            {filteredCategories.length === 0 ? (
              <Text style={styles.emptyText}>No categories match. Try another search.</Text>
            ) : (
              filteredCategories.map((category) => (
                <TouchableOpacity
                  key={category.id}
                  style={[
                    styles.option,
                    categoryId === category.id ? styles.optionSelected : null,
                  ]}
                  onPress={() => handleSelect(category.id, category.name)}
                >
                  <Text style={styles.optionIcon}>{category.icon || '📁'}</Text>
                  <View style={styles.optionTextWrap}>
                    <Text style={styles.optionName}>{category.name}</Text>
                    <Text style={styles.optionDesc}>{category.description}</Text>
                  </View>
                  {categoryId === category.id ? (
                    <Text style={styles.checkmark}>✓</Text>
                  ) : null}
                </TouchableOpacity>
              ))
            )}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  valueText: {
    fontSize: 16,
    color: '#1C1C1E',
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    backgroundColor: '#fff',
  },
  selectorError: {
    borderColor: '#dc3545',
  },
  selectedText: {
    flex: 1,
    fontSize: 16,
    color: '#1C1C1E',
    marginRight: 8,
  },
  placeholderText: {
    flex: 1,
    fontSize: 16,
    color: '#999',
    marginRight: 8,
  },
  dropdownIcon: {
    fontSize: 12,
    color: '#666',
  },
  helperText: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 8,
    lineHeight: 17,
  },
  errorText: {
    fontSize: 12,
    color: '#dc3545',
    marginTop: 6,
  },
  dropdown: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    backgroundColor: '#fff',
    maxHeight: 320,
    overflow: 'hidden',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#E1E5E9',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchIcon: {
    fontSize: 16,
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: '#1C1C1E',
    paddingVertical: 6,
  },
  list: {
    maxHeight: 260,
  },
  emptyText: {
    padding: 16,
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  option: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E1E5E9',
  },
  optionSelected: {
    backgroundColor: '#EEF6FF',
  },
  optionIcon: {
    fontSize: 22,
    marginRight: 10,
    marginTop: 2,
  },
  optionTextWrap: {
    flex: 1,
  },
  optionName: {
    fontSize: 15,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  optionDesc: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
  },
  checkmark: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '700',
    marginLeft: 8,
  },
});

export default BusinessCategoryPicker;
